import React from 'react';
import { Event, RsvpResponse } from '../../types';
import { useTenant } from '../../contexts/TenantContext';
import { useCurrentUser } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useRsvp, useCancelEvent } from '../../hooks/useEvents';
import { 
  Trophy, 
  Clock, 
  MapPin, 
  Check, 
  X, 
  HelpCircle, 
  Lock 
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { cn, safeFormat } from '../../lib/utils';
import toast from 'react-hot-toast';

interface EventCardProps {
  event: Event;
  userRole?: string;
  onClick?: (event: Event) => void;
}

export const EventCard: React.FC<EventCardProps> = ({ event, userRole, onClick }) => {
  const { node } = useTenant();
  const { user } = useCurrentUser();
  const { can, isAdmin: isGlobalAdmin } = usePermissions();
  const isCreator = Boolean(user?.id && (event.creatorId === user.id || event.createdBy === user.id || event.userId === user.id));
  const canManageEvent = isGlobalAdmin || can('EVENT_MANAGE') || can('create_event') || can('create_events') || can('manage_events') || isCreator;

  const rsvpMutation = useRsvp(node?.id || '', event.id);
  const cancelMutation = useCancelEvent(node?.id || '', event.id);
  const isCancelled = event.status === 'CANCELLED';
  const terms = node?.config?.terminology;
  const isFromParent = Boolean(event.ownerNodeId && node?.id && event.ownerNodeId !== node.id);
  const currentRsvp = event.myRsvp || event.userRsvp;

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canManageEvent) return;
    if (window.confirm('Are you sure you want to cancel this event?')) {
      cancelMutation.mutate(undefined);
    }
  };

  const handleRsvp = (e: React.MouseEvent, response: RsvpResponse) => {
    e.stopPropagation();
    if (event.locked || isCancelled) return;
    rsvpMutation.mutate(response);
  };

  return (
    <Card 
      className={cn(
        "relative overflow-hidden transition-all hover:shadow-md cursor-pointer group",
        isCancelled && "bg-gray-50 opacity-75 dark:bg-slate-950",
        isFromParent && "border-l-4 border-l-gray-400"
      )}
      onClick={() => onClick?.(event)}
    >
      <div className="flex gap-4">
        {/* Date Block */}
        <div className={cn(
          "flex-shrink-0 w-14 h-16 rounded-lg flex flex-col items-center justify-center font-mono",
          isCancelled ? "bg-gray-200 text-gray-500" : "bg-navy-900 text-white"
        )}>
          <span className="text-[10px] uppercase font-bold tracking-widest opacity-70">
            {safeFormat(event.startTime, 'MMM')}
          </span>
          <span className="text-xl font-bold">
            {safeFormat(event.startTime, 'd')}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className={cn(
                "font-semibold text-gray-900 truncate group-hover:text-brand-primary transition-colors dark:text-white",
                isCancelled && "line-through text-gray-500"
              )}>
                {event.title}
              </h3>
              {event.isJoint && <Badge variant="blue" className="text-[9px] uppercase tracking-wider font-bold">Joint</Badge>}
              {event.eventType === 'MATCHUP' && <Trophy size={14} className="text-amber-600" />}
              {isCancelled && <Badge variant="gray">Cancelled</Badge>}
              {isFromParent && (
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-100 px-1.5 py-0.5 rounded dark:bg-slate-800">
                  {terms?.parentNode || 'From'}: {event.ownerNodeName}
                </span>
              )}
            </div>
            
            {canManageEvent && !isCancelled && !isFromParent && (
              <button 
                onClick={handleCancel}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Cancel Event"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {(event.isJoint || event.eventType === 'MATCHUP') && event.homeNodeName && event.awayNodeName && (
            <div className="flex items-center gap-2 text-xs font-bold text-blue-700 bg-blue-50/50 px-2 py-1 rounded w-fit mb-2">
              <span>{event.homeNodeName}</span>
              <span className="text-gray-400 font-normal">v</span>
              <span>{event.awayNodeName}</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>
                {event.allDay ? 'All Day' : `${safeFormat(event.startTime, 'h:mm a')} - ${safeFormat(event.endTime, 'h:mm a')}`}
              </span>
            </div>
            {event.location && (
              <div className="flex items-center gap-1">
                <MapPin size={14} />
                <span className="truncate">{event.location}</span>
              </div>
            )}
          </div>

          {/* RSVP Section */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              {!isFromParent && !isCancelled && !event.locked ? (
                <div className="flex items-center bg-gray-100 p-0.5 rounded-lg dark:bg-slate-800">
                  <button
                    onClick={(e) => handleRsvp(e, 'YES')}
                    disabled={rsvpMutation.isPending}
                    className={cn(
                      "px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1",
                      currentRsvp === 'YES' 
                        ? "bg-white text-green-700 shadow-sm dark:bg-slate-900" 
                        : "text-gray-500 hover:text-gray-700"
                    )}
                  >
                    {currentRsvp === 'YES' && <Check size={12} />}
                    Yes
                  </button>
                  <button
                    onClick={(e) => handleRsvp(e, 'MAYBE')}
                    disabled={rsvpMutation.isPending}
                    className={cn(
                      "px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1",
                      currentRsvp === 'MAYBE' 
                        ? "bg-white text-amber-700 shadow-sm dark:bg-slate-900" 
                        : "text-gray-500 hover:text-gray-700"
                    )}
                  >
                    {currentRsvp === 'MAYBE' && <HelpCircle size={12} />}
                    Maybe
                  </button>
                  <button
                    onClick={(e) => handleRsvp(e, 'NO')}
                    disabled={rsvpMutation.isPending}
                    className={cn(
                      "px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1",
                      currentRsvp === 'NO' 
                        ? "bg-white text-red-700 shadow-sm dark:bg-slate-900" 
                        : "text-gray-500 hover:text-gray-700"
                    )}
                  >
                    {currentRsvp === 'NO' && <X size={12} />}
                    No
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-gray-400 italic">
                  {event.locked && <Lock size={12} />}
                  {isCancelled ? 'Event cancelled' : event.locked ? 'RSVPs locked' : 'Read-only event'}
                </div>
              )}
            </div>

            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">
              {event.rsvpCounts?.YES ?? 0} Attending
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
